import { useEffect, useState, useRef } from "react";
import { createPortal } from "react-dom";
import { saveProjectDetails } from "../Api/ProjectMaster";
import {
  getProjectTypesByOrg,
  getStatusesByTypeAndOrg,
  getSubStatusesByTypeStatusAndOrg,
} from "../Api/ProjectConfigurationController";
import { getSectorsByOrganisation } from "../Api/ProjectSector";
import { Container, Form, Button, Row, Col, Card } from "react-bootstrap";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import Sample14 from "../Components/Logo";
import {
  fetchCustomersByOrgId,
  fetchResourceByOrgId,
  fetchCompanyCodesById,
} from "../Api/InvoiceApi";
import { useAuth } from "../Context/AuthContext";
import { getTeamsWithMembersByOrg } from "../Api/TeamMaster";
import Select from "react-select";
import { FaUsers, FaInfoCircle } from "react-icons/fa";
import { getContactGuid } from "../Api/TaskMaster";

const ProjectMaster = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [teams, setTeams] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [hoveredTeam, setHoveredTeam] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const teamSelectRef = useRef(null);
  const hoverTimeoutRef = useRef(null);

  const userDetails = JSON.parse(localStorage.getItem("userDetails"));
  const userId = userDetails?.userId;
  const orgId = userDetails?.organisationId;

  const [customer, setCustomer] = useState([]);
  const [projectTypes, setProjectTypes] = useState([]);
  const [projectStatuses, setProjectStatuses] = useState([]);
  const [projectSubStatuses, setProjectSubStatuses] = useState([]);
  const [resource, setResource] = useState([]);
  const [company, setCompany] = useState([]);
  
  // State for sectors
  const [sectors, setSectors] = useState([]);
  const [selectedSector, setSelectedSector] = useState(null);
  const [loadingSectors, setLoadingSectors] = useState(false);

  // State for Contact GUID
  const [contactGuid, setContactGuid] = useState(null);
  const [loadingContactGuid, setLoadingContactGuid] = useState(false);

  const { user } = useAuth();

  // State for loading dropdowns
  const [loadingStatuses, setLoadingStatuses] = useState(false);
  const [loadingSubStatuses, setLoadingSubStatuses] = useState(false);

  // Selected values for React Select
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedProjectType, setSelectedProjectType] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [selectedSubStatus, setSelectedSubStatus] = useState(null);
  const [selectedSalesperson, setSelectedSalesperson] = useState(null);
  const [selectedManager, setSelectedManager] = useState(null);
  const [selectedCompany, setSelectedCompany] = useState(null);

  const [projectData, setProjectData] = useState({
    prTitle: "",
    prDescription: "",
    prTags: "",
    prStartDate: "",
    prEndDate: "",
    prClient: "",
    prTypes: "",
    usersId: "",
    orgId: String(orgId) || "",
    teamAssigned: "",
    projectStatus: "",
    projectSubStatus: "",
    projectCategory: "",
    comments: "",
    salespersonCode: "",
    currentProjectManager: "",
    organisationId: user?.organisationId,
    companyCode: "",
    projectPriority: "",
    projectSector: "",
  });

  // Helper function to sort options alphabetically by label
  const sortOptions = (options) => {
    return [...options].sort((a, b) => a.label.localeCompare(b.label));
  };

  // Convert data to React Select options format with display names
  const formatSelectOptions = (data, valueKey, labelKey) => {
    if (!data || !Array.isArray(data)) return [];
    return data
      .map((item) => ({
        value: item[valueKey],
        label: `${item[labelKey]}${item.code ? ` (${item.code})` : ""}`,
        originalData: item,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  };

  // Format sector options (sectorName as label, sectorGuid as value)
  const sectorOptions = sectors.map((sector) => ({
    value: sector.sectorGuid,
    label: sector.sectorName,
    originalData: sector,
  }));

  // Prepare options for different dropdowns
  const companyOptions = formatSelectOptions(company, "companyCode", "name");
  const customerOptions = formatSelectOptions(customer, "customerCode", "name");
  const resourceOptions = formatSelectOptions(resource, "resourceId", "name");
  const projectTypeOptions = formatSelectOptions(
    projectTypes,
    "projectTypeGuid",
    "projectTypeDesc"
  );
  const statusOptions = formatSelectOptions(
    projectStatuses,
    "projectStatusGuid",
    "projectStatusDesc"
  );
  const subStatusOptions = formatSelectOptions(
    projectSubStatuses,
    "projectSubStatusGuid",
    "projectSubStatusDesc"
  );
  const teamOptions = teams
    .map((team) => ({
      value: team.teamId,
      label: `${team.teamName} (${team.members?.length || 0} members)`,
      teamData: team,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  // Fetch Contact GUID on component mount
  useEffect(() => {
    const fetchContactGuid = async () => {
      if (orgId && userId) {
        setLoadingContactGuid(true);
        try {
          const response = await getContactGuid(orgId, userId);
          if (response && response.contactGuid) {
            setContactGuid(response.contactGuid);
            setProjectData((prev) => ({
              ...prev,
              usersId: response.contactGuid,
            }));
          } else {
            console.error("No contact GUID received");
            toast.error("Unable to fetch contact information");
          }
        } catch (error) {
          console.error("Error fetching contact GUID:", error);
          toast.error("Failed to load contact information");
        } finally {
          setLoadingContactGuid(false);
        }
      }
    };

    fetchContactGuid();
  }, [orgId, userId]);

  // Fetch sectors when orgId is available
  useEffect(() => {
    const fetchSectors = async () => {
      if (orgId) {
        setLoadingSectors(true);
        try {
          const sectorsData = await getSectorsByOrganisation(parseInt(orgId));
          setSectors(sectorsData || []);
        } catch (error) {
          console.error("Error fetching sectors:", error);
          toast.error("Failed to load project sectors");
          setSectors([]);
        } finally {
          setLoadingSectors(false);
        }
      }
    };

    fetchSectors();
  }, [orgId]);

  // Generic handler for regular form inputs (text, date, select)
  const handleChange = (e) => {
    const { name, value } = e.target;
    setProjectData((prev) => ({ ...prev, [name]: value }));
  };

  // Generic handler for React Select changes
  const handleSelectChange = (selectedOption, fieldName, valueKey) => {
    const value = selectedOption?.value || "";
    
    setProjectData((prev) => ({
      ...prev,
      [fieldName]: value,
    }));

    // Update specific selected state
    switch (fieldName) {
      case "companyCode":
        setSelectedCompany(selectedOption);
        break;
      case "prClient":
        setSelectedClient(selectedOption);
        break;
      case "salespersonCode":
        setSelectedSalesperson(selectedOption);
        break;
      case "currentProjectManager":
        setSelectedManager(selectedOption);
        break;
      case "prTypes":
        setSelectedProjectType(selectedOption);
        // Reset status and sub-status when project type changes
        setProjectData((prev) => ({
          ...prev,
          projectStatus: "",
          projectSubStatus: "",
        }));
        setSelectedStatus(null);
        setSelectedSubStatus(null);
        setProjectStatuses([]);
        setProjectSubStatuses([]);
        if (selectedOption?.value) {
          fetchProjectStatuses(selectedOption.value);
        }
        break;
      case "projectStatus":
        setSelectedStatus(selectedOption);
        // Reset sub-status when status changes
        setProjectData((prev) => ({
          ...prev,
          projectSubStatus: "",
        }));
        setSelectedSubStatus(null);
        setProjectSubStatuses([]);
        if (projectData.prTypes && selectedOption?.value) {
          fetchProjectSubStatuses(projectData.prTypes, selectedOption.value);
        }
        break;
      case "projectSubStatus":
        setSelectedSubStatus(selectedOption);
        break;
      case "projectSector":
        setSelectedSector(selectedOption);
        break;
      default:
        break;
    }
  };

  // Handle project type change
  const handleProjectTypeChange = (selectedOption) => {
    handleSelectChange(selectedOption, "prTypes", "projectTypeGuid");
  };

  // Handle project status change
  const handleProjectStatusChange = (selectedOption) => {
    handleSelectChange(selectedOption, "projectStatus", "projectStatusGuid");
  };

  // Handle project sub-status change
  const handleProjectSubStatusChange = (selectedOption) => {
    handleSelectChange(selectedOption, "projectSubStatus", "projectSubStatusGuid");
  };

  // Handle sector change
  const handleSectorChange = (selectedOption) => {
    handleSelectChange(selectedOption, "projectSector", "sectorGuid");
  };

  // Fetch project status based on selected project type using new API
  const fetchProjectStatuses = async (projectTypeGuid) => {
    if (!projectTypeGuid || !orgId) {
      setProjectStatuses([]);
      return;
    }

    setLoadingStatuses(true);
    try {
      const statuses = await getStatusesByTypeAndOrg(projectTypeGuid, orgId);
      setProjectStatuses(statuses || []);
    } catch (error) {
      console.error("Error fetching project statuses:", error);
      toast.error("Failed to load project statuses");
      setProjectStatuses([]);
    } finally {
      setLoadingStatuses(false);
    }
  };

  // Fetch project sub-status based on project type and status using new API
  const fetchProjectSubStatuses = async (projectTypeGuid, projectStatusGuid) => {
    if (!projectTypeGuid || !projectStatusGuid || !orgId) {
      setProjectSubStatuses([]);
      return;
    }

    setLoadingSubStatuses(true);
    try {
      const subStatuses = await getSubStatusesByTypeStatusAndOrg(
        projectTypeGuid,
        projectStatusGuid,
        orgId
      );
      setProjectSubStatuses(subStatuses || []);
    } catch (error) {
      console.error("Error fetching project sub-statuses:", error);
      toast.error("Failed to load project sub-statuses");
      setProjectSubStatuses([]);
    } finally {
      setLoadingSubStatuses(false);
    }
  };

  const fetchDropdownData = async () => {
    try {
      const [customersRes, resourcesRes, companiesRes, teamsData] =
        await Promise.all([
          fetchCustomersByOrgId(user?.organisationId),
          fetchResourceByOrgId(user?.organisationId),
          fetchCompanyCodesById(user?.organisationId),
          getTeamsWithMembersByOrg(user?.organisationId),
        ]);

      // Sort data alphabetically before setting state
      const sortedCustomers = [...(customersRes || [])].sort((a, b) => 
        a.name?.localeCompare(b.name)
      );
      const sortedResources = [...(resourcesRes || [])].sort((a, b) => 
        a.name?.localeCompare(b.name)
      );
      const sortedCompanies = [...(companiesRes || [])].sort((a, b) => 
        a.name?.localeCompare(b.name)
      );

      setCustomer(sortedCustomers);
      setResource(sortedResources);
      setCompany(sortedCompanies);
      setTeams(teamsData || []);
    } catch (error) {
      console.error("Error fetching dropdown data:", error);
      toast.error("Failed to load dropdown data");
      setCustomer([]);
      setResource([]);
      setCompany([]);
      setTeams([]);
    }
  };

  const fetchProjectTypesList = async () => {
    if (!orgId) return;
    
    try {
      const types = await getProjectTypesByOrg(orgId);
      const sortedTypes = [...(types || [])].sort((a, b) => 
        a.projectTypeDesc?.localeCompare(b.projectTypeDesc)
      );
      setProjectTypes(sortedTypes);
    } catch (error) {
      console.error("Error fetching project types:", error);
      toast.error("Failed to load project types");
      setProjectTypes([]);
    }
  };

  useEffect(() => {
    fetchDropdownData();
    fetchProjectTypesList();
  }, [orgId]);

  // Handle team selection
  const handleTeamChange = (selectedOption) => {
    setSelectedTeam(selectedOption);
    setProjectData((prev) => ({
      ...prev,
      teamAssigned: selectedOption?.value || "",
    }));
    setHoveredTeam(null);
  };

  // Handle mouse enter for tooltip
  const handleMouseEnter = (event, teamData) => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }

    const rect = event.currentTarget.getBoundingClientRect();
    setTooltipPosition({
      top: rect.top - 10,
      left: rect.left + rect.width + 10,
    });
    setHoveredTeam(teamData);
  };

  const handleMouseLeave = () => {
    hoverTimeoutRef.current = setTimeout(() => {
      setHoveredTeam(null);
    }, 100);
  };

  // Handle dropdown open/close
  const handleMenuOpen = () => {
    setIsDropdownOpen(true);
  };

  const handleMenuClose = () => {
    setIsDropdownOpen(false);
    setHoveredTeam(null);
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
  };

  // Custom option component with hover tooltip for teams
  const CustomOption = (props) => {
    const { data, innerRef, innerProps, isFocused } = props;

    return (
      <div
        ref={innerRef}
        {...innerProps}
        style={{
          padding: "10px",
          backgroundColor: isFocused ? "#f0f0f0" : "white",
          cursor: "pointer",
          position: "relative",
        }}
        onMouseEnter={(e) => handleMouseEnter(e, data.teamData)}
        onMouseLeave={handleMouseLeave}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <span>
            <FaUsers style={{ marginRight: "8px", color: "#007bff" }} />
            {data.label}
          </span>
          <FaInfoCircle style={{ color: "#6c757d", fontSize: "12px" }} />
        </div>
      </div>
    );
  };

  // Custom single value display for teams
  const CustomSingleValue = ({ data }) => (
    <div style={{ display: "flex", alignItems: "center" }}>
      <FaUsers style={{ marginRight: "8px", color: "#007bff" }} />
      <span>{data.label}</span>
    </div>
  );

  // Render team members tooltip
  const renderTeamMembersTooltip = (team) => {
    if (!team) return null;

    return (
      <Card
        style={{
          backgroundColor: "white",
          border: "1px solid #ddd",
          borderRadius: "8px",
          boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
          minWidth: "320px",
          maxWidth: "450px",
        }}
      >
        <Card.Header
          style={{
            backgroundColor: "#007bff",
            color: "white",
            fontWeight: "bold",
            padding: "10px 15px",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <FaUsers />
            <span>
              {team.teamName} - Members ({team.members?.length || 0})
            </span>
          </div>
        </Card.Header>
        <Card.Body
          style={{ maxHeight: "350px", overflowY: "auto", padding: "10px" }}
        >
          {team.members && team.members.length > 0 ? (
            <div style={{ fontSize: "13px" }}>
              {team.members.map((member, index) => (
                <div
                  key={member.memberId}
                  style={{
                    padding: "10px",
                    borderBottom:
                      index < team.members.length - 1
                        ? "1px solid #e9ecef"
                        : "none",
                    transition: "background-color 0.2s",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = "#f8f9fa";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "transparent";
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                    }}
                  >
                    <div
                      style={{
                        width: "32px",
                        height: "32px",
                        borderRadius: "50%",
                        backgroundColor: "#e9ecef",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontWeight: "bold",
                        color: "#495057",
                      }}
                    >
                      {member.memberId || index + 1}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: "600", color: "#212529" }}>
                        {member.memberName}
                      </div>
                      {member.emailId && (
                        <div
                          style={{
                            fontSize: "11px",
                            color: "#6c757d",
                            marginTop: "2px",
                          }}
                        >
                          {member.emailId}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p
              style={{
                textAlign: "center",
                color: "#999",
                margin: 0,
                padding: "20px",
              }}
            >
              No members in this team
            </p>
          )}
        </Card.Body>
      </Card>
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!contactGuid) {
      toast.error("Contact information not loaded. Please refresh and try again.");
      return;
    }

    const submitData = {
      ...projectData,
      usersId: contactGuid,
    };

    if (
      !submitData.prTitle ||
      !submitData.prDescription ||
      !submitData.prStartDate ||
      !submitData.prEndDate ||
      !submitData.projectCategory ||
      !submitData.companyCode ||
      !submitData.projectPriority ||
      !submitData.prTypes ||
      !submitData.projectStatus ||
      !submitData.currentProjectManager
    ) {
      toast.error("Please fill all required fields");
      return;
    }

    if (submitData.projectCategory === "External") {
      if (!submitData.prClient) {
        toast.error("Please select a client for external project");
        return;
      }
      if (!submitData.salespersonCode) {
        toast.error("Please select a salesperson for external project");
        return;
      }
    }

    setLoading(true);
    try {
      const response = await saveProjectDetails(submitData, contactGuid);

      if (response && response.projectId && response.projectName) {
        toast.success(
          <div>
            <strong>✅ Project Created Successfully!</strong>
            <br />
            <span>
              📋 Project ID: <strong>{response.projectId}</strong>
            </span>
            <br />
            <span>
              📝 Project Name: <strong>{response.projectName}</strong>
            </span>
          </div>,
          {
            autoClose: 5000,
            position: "top-right",
          }
        );
      } else {
        toast.success("🦄 Project Added Successfully");
      }

      // Reset form
      setProjectData({
        prTitle: "",
        prDescription: "",
        prTags: "",
        prStartDate: "",
        prEndDate: "",
        prClient: "",
        prTypes: "",
        usersId: contactGuid,
        orgId: String(orgId) || "",
        teamAssigned: "",
        projectStatus: "",
        projectSubStatus: "",
        projectCategory: "",
        comments: "",
        salespersonCode: "",
        currentProjectManager: "",
        organisationId: user?.organisationId,
        companyCode: "",
        projectPriority: "",
        projectSector: "",
      });
      
      // Reset all selected values
      setSelectedCompany(null);
      setSelectedClient(null);
      setSelectedSalesperson(null);
      setSelectedManager(null);
      setSelectedProjectType(null);
      setSelectedStatus(null);
      setSelectedSubStatus(null);
      setSelectedTeam(null);
      setSelectedSector(null);
      setProjectStatuses([]);
      setProjectSubStatuses([]);

      setTimeout(() => {
        navigate("/viewProjects");
      }, 2000);
    } catch (error) {
      toast.error(`🦄 Error saving project: ${error.message || error}`);
      console.error("Save error:", error);
    } finally {
      setLoading(false);
    }
  };

  // Add animation styles
  useEffect(() => {
    const style = document.createElement("style");
    style.textContent = `
      @keyframes fadeIn {
        from {
          opacity: 0;
          transform: translateX(-10px);
        }
        to {
          opacity: 1;
          transform: translateX(0);
        }
      }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, []);

  return (
    <div className="main-content expand">
      <Container fluid className="fields-container">
        <h2 className="fields-title">Project Master</h2>

        {(loading || loadingContactGuid) && (
          <div className="loading-overlay">
            <Sample14 />
            {loadingContactGuid && (
              <p style={{ marginTop: "10px" }}>
                Loading contact information...
              </p>
            )}
          </div>
        )}

        {/* Tooltip rendered via Portal */}
        {hoveredTeam &&
          isDropdownOpen &&
          createPortal(
            <div
              style={{
                position: "fixed",
                zIndex: 9999,
                top: tooltipPosition.top,
                left: tooltipPosition.left,
                animation: "fadeIn 0.2s ease-in-out",
              }}
              onMouseEnter={() => {
                if (hoverTimeoutRef.current) {
                  clearTimeout(hoverTimeoutRef.current);
                }
              }}
              onMouseLeave={() => {
                setHoveredTeam(null);
              }}
            >
              {renderTeamMembersTooltip(hoveredTeam)}
            </div>,
            document.body
          )}

        <Form onSubmit={handleSubmit} className="fields-form">
          {/* Company Code and Category */}
          <Row>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>
                  Company Code <span className="text-red">**</span>
                </Form.Label>
                <Select
                  options={companyOptions}
                  value={selectedCompany}
                  onChange={(option) => handleSelectChange(option, "companyCode")}
                  placeholder="Search and select company..."
                  isClearable
                  isSearchable
                  isDisabled={!user?.organisationId || loadingContactGuid}
                  noOptionsMessage={() => "No companies found"}
                  styles={{
                    control: (base) => ({
                      ...base,
                      borderColor: "#ced4da",
                      "&:hover": {
                        borderColor: "#86b7fe",
                      },
                    }),
                  }}
                />
              </Form.Group>
            </Col>

            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>
                  Category <span className="text-red">**</span>
                </Form.Label>
                <Form.Select
                  name="projectCategory"
                  value={projectData.projectCategory}
                  onChange={handleChange}
                  required
                  disabled={loadingContactGuid}
                >
                  <option value="">Select Category</option>
                  <option value="Internal">Internal</option>
                  <option value="External">External</option>
                </Form.Select>
              </Form.Group>
            </Col>

            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Project Sector</Form.Label>
                <Select
                  options={sectorOptions}
                  value={selectedSector}
                  onChange={handleSectorChange}
                  placeholder={
                    loadingSectors
                      ? "Loading sectors..."
                      : "Search and select project sector..."
                  }
                  isClearable
                  isSearchable
                  isDisabled={loadingSectors || loadingContactGuid}
                  isLoading={loadingSectors}
                  noOptionsMessage={() =>
                    loadingSectors ? "Loading..." : "No sectors found"
                  }
                  styles={{
                    control: (base) => ({
                      ...base,
                      borderColor: "#ced4da",
                      "&:hover": {
                        borderColor: "#86b7fe",
                      },
                    }),
                  }}
                />
                <small className="text-muted">
                  Select the industry sector for this project
                </small>
              </Form.Group>
            </Col>
          </Row>

          {/* Basic Details */}
          <Row>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>
                  Project Title <span className="text-red">**</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  name="prTitle"
                  value={projectData.prTitle}
                  onChange={handleChange}
                  required
                  disabled={loadingContactGuid}
                />
              </Form.Group>
            </Col>

            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>
                  Project Description <span className="text-red">**</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  name="prDescription"
                  value={projectData.prDescription}
                  onChange={handleChange}
                  required
                  disabled={loadingContactGuid}
                />
              </Form.Group>
            </Col>

            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Project Tags</Form.Label>
                <Form.Control
                  type="text"
                  name="prTags"
                  value={projectData.prTags}
                  onChange={handleChange}
                  disabled={loadingContactGuid}
                />
              </Form.Group>
            </Col>
          </Row>

          {/* Dates */}
          <Row>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>
                  Start Date <span className="text-red">**</span>
                </Form.Label>
                <Form.Control
                  type="date"
                  name="prStartDate"
                  value={projectData.prStartDate}
                  onChange={handleChange}
                  required
                  disabled={loadingContactGuid}
                />
              </Form.Group>
            </Col>

            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>
                  End Date <span className="text-red">**</span>
                </Form.Label>
                <Form.Control
                  type="date"
                  name="prEndDate"
                  value={projectData.prEndDate}
                  onChange={handleChange}
                  required
                  disabled={loadingContactGuid}
                />
              </Form.Group>
            </Col>

            {projectData.projectCategory !== "Internal" && (
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    Client <span className="text-red">**</span>
                  </Form.Label>
                  <Select
                    options={customerOptions}
                    value={selectedClient}
                    onChange={(option) => handleSelectChange(option, "prClient")}
                    placeholder="Search and select client..."
                    isClearable
                    isSearchable
                    isDisabled={!user?.organisationId || loadingContactGuid}
                    required={projectData.projectCategory === "External"}
                    noOptionsMessage={() => "No clients found"}
                    styles={{
                      control: (base) => ({
                        ...base,
                        borderColor: "#ced4da",
                        "&:hover": {
                          borderColor: "#86b7fe",
                        },
                      }),
                    }}
                  />
                </Form.Group>
              </Col>
            )}
          </Row>

          {/* Type, Team, Priority */}
          <Row>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>
                  Project Type <span className="text-red">**</span>
                </Form.Label>
                <Select
                  options={projectTypeOptions}
                  value={selectedProjectType}
                  onChange={handleProjectTypeChange}
                  placeholder="Search and select project type..."
                  isClearable
                  isSearchable
                  isDisabled={!user?.organisationId || loadingContactGuid}
                  noOptionsMessage={() => "No project types found"}
                  styles={{
                    control: (base) => ({
                      ...base,
                      borderColor: "#ced4da",
                      "&:hover": {
                        borderColor: "#86b7fe",
                      },
                    }),
                  }}
                />
              </Form.Group>
            </Col>

            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Team Assigned</Form.Label>
                <div ref={teamSelectRef}>
                  <Select
                    options={teamOptions}
                    value={selectedTeam}
                    onChange={handleTeamChange}
                    onMenuOpen={handleMenuOpen}
                    onMenuClose={handleMenuClose}
                    placeholder="Search and select team..."
                    isClearable
                    isSearchable
                    isDisabled={loadingContactGuid}
                    components={{
                      Option: CustomOption,
                      SingleValue: CustomSingleValue,
                    }}
                    styles={{
                      control: (base) => ({
                        ...base,
                        minHeight: "38px",
                        borderColor: "#ced4da",
                        "&:hover": {
                          borderColor: "#86b7fe",
                        },
                      }),
                      option: (base, state) => ({
                        ...base,
                        backgroundColor: state.isFocused ? "#f0f0f0" : "white",
                        "&:active": {
                          backgroundColor: "#e0e0e0",
                        },
                      }),
                      menu: (base) => ({
                        ...base,
                        zIndex: 9998,
                      }),
                    }}
                  />
                </div>
                <small className="text-muted">
                  Hover over any team to see its members
                </small>
              </Form.Group>
            </Col>

            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>
                  Project Priority <span className="text-red">**</span>
                </Form.Label>
                <Form.Select
                  name="projectPriority"
                  value={projectData.projectPriority}
                  onChange={handleChange}
                  required
                  disabled={loadingContactGuid}
                >
                  <option value="">Select Priority</option>
                  <option value="Very High">Very High</option>
                  <option value="High">High</option>
                  <option value="Medium">Medium</option>
                  <option value="Low">Low</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          {/* Status and Sub Status */}
          <Row>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>
                  Status <span className="text-red">**</span>
                </Form.Label>
                <Select
                  options={statusOptions}
                  value={selectedStatus}
                  onChange={handleProjectStatusChange}
                  placeholder={
                    loadingStatuses
                      ? "Loading statuses..."
                      : !projectData.prTypes
                      ? "Select project type first"
                      : "Search and select status..."
                  }
                  isClearable
                  isSearchable
                  isDisabled={
                    !projectData.prTypes || loadingStatuses || loadingContactGuid
                  }
                  isLoading={loadingStatuses}
                  noOptionsMessage={() => 
                    loadingStatuses ? "Loading..." : "No statuses found"
                  }
                  styles={{
                    control: (base) => ({
                      ...base,
                      borderColor: "#ced4da",
                      "&:hover": {
                        borderColor: "#86b7fe",
                      },
                    }),
                  }}
                />
              </Form.Group>
            </Col>

            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Sub Status</Form.Label>
                <Select
                  options={subStatusOptions}
                  value={selectedSubStatus}
                  onChange={handleProjectSubStatusChange}
                  placeholder={
                    loadingSubStatuses
                      ? "Loading sub-statuses..."
                      : !projectData.projectStatus
                      ? "Select status first"
                      : "Search and select sub-status..."
                  }
                  isClearable
                  isSearchable
                  isDisabled={
                    !projectData.projectStatus || loadingSubStatuses || loadingContactGuid
                  }
                  isLoading={loadingSubStatuses}
                  noOptionsMessage={() =>
                    loadingSubStatuses ? "Loading..." : "No sub-statuses found"
                  }
                  styles={{
                    control: (base) => ({
                      ...base,
                      borderColor: "#ced4da",
                      "&:hover": {
                        borderColor: "#86b7fe",
                      },
                    }),
                  }}
                />
              </Form.Group>
            </Col>

            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Comments</Form.Label>
                <Form.Control
                  type="text"
                  name="comments"
                  value={projectData.comments}
                  onChange={handleChange}
                  disabled={loadingContactGuid}
                />
              </Form.Group>
            </Col>
          </Row>

          <Row>
            {projectData.projectCategory !== "Internal" && (
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    Salesperson <span className="text-red">**</span>
                  </Form.Label>
                  <Select
                    options={resourceOptions}
                    value={selectedSalesperson}
                    onChange={(option) => handleSelectChange(option, "salespersonCode")}
                    placeholder="Search and select salesperson..."
                    isClearable
                    isSearchable
                    isDisabled={!user?.organisationId || loadingContactGuid}
                    required={projectData.projectCategory === "External"}
                    noOptionsMessage={() => "No salespersons found"}
                    styles={{
                      control: (base) => ({
                        ...base,
                        borderColor: "#ced4da",
                        "&:hover": {
                          borderColor: "#86b7fe",
                        },
                      }),
                    }}
                  />
                </Form.Group>
              </Col>
            )}

            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>
                  Project Manager <span className="text-red">**</span>
                </Form.Label>
                <Select
                  options={resourceOptions}
                  value={selectedManager}
                  onChange={(option) => handleSelectChange(option, "currentProjectManager")}
                  placeholder="Search and select project manager..."
                  isClearable
                  isSearchable
                  isDisabled={!user?.organisationId || loadingContactGuid}
                  required
                  noOptionsMessage={() => "No managers found"}
                  styles={{
                    control: (base) => ({
                      ...base,
                      borderColor: "#ced4da",
                      "&:hover": {
                        borderColor: "#86b7fe",
                      },
                    }),
                  }}
                />
              </Form.Group>
            </Col>
          </Row>

          {/* Hidden field to show Contact GUID info */}
          {contactGuid && (
            <Row>
              <Col md={12}>
                <small className="text-muted">
                  Contact ID: {contactGuid.substring(0, 8)}... (Auto-loaded)
                </small>
              </Col>
            </Row>
          )}

          {/* Submit Button */}
          <div className="fields-btn-container">
            <Button
              variant="primary"
              type="submit"
              className="fields-submit"
              disabled={loading || loadingContactGuid || !contactGuid}
            >
              {loading ? "Saving..." : "Save Project"}
            </Button>
          </div>
        </Form>
      </Container>
    </div>
  );
};

export default ProjectMaster;


// import { useEffect, useState, useRef } from "react";
// import { createPortal } from "react-dom";
// import { saveProjectDetails } from "../Api/ProjectMaster";
// import {
//   getProjectTypes,
//   getProjectStatus,
//   getProjectSubStatus,
// } from "../Api/ProjectMaster";
// import { getSectorsByOrganisation } from "../Api/ProjectSector"; // ✅ Import sector API
// import { Container, Form, Button, Row, Col, Card } from "react-bootstrap";
// import { toast } from "react-toastify";
// import { useNavigate } from "react-router-dom";
// import Sample14 from "../Components/Logo";
// import {
//   fetchCustomersByOrgId,
//   fetchResourceByOrgId,
//   fetchCompanyCodesById,
// } from "../Api/InvoiceApi";
// import { useAuth } from "../Context/AuthContext";
// import { getTeamsWithMembersByOrg } from "../Api/TeamMaster";
// import Select from "react-select";
// import { FaUsers, FaInfoCircle } from "react-icons/fa";
// import { getContactGuid } from "../Api/TaskMaster";

// const ProjectMaster = () => {
//   const navigate = useNavigate();
//   const [loading, setLoading] = useState(false);
//   const [teams, setTeams] = useState([]);
//   const [selectedTeam, setSelectedTeam] = useState(null);
//   const [hoveredTeam, setHoveredTeam] = useState(null);
//   const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
//   const [isDropdownOpen, setIsDropdownOpen] = useState(false);
//   const teamSelectRef = useRef(null);
//   const hoverTimeoutRef = useRef(null);

//   const userDetails = JSON.parse(localStorage.getItem("userDetails"));
//   const userId = userDetails?.userId;
//   const orgId = userDetails?.organisationId;

//   const [customer, setCustomer] = useState([]);
//   const [projectTypes, setProjectTypes] = useState([]);
//   const [projectStatuses, setProjectStatuses] = useState([]);
//   const [projectSubStatuses, setProjectSubStatuses] = useState([]);
//   const [resource, setResource] = useState([]);
//   const [company, setCompany] = useState([]);
  
//   // ✅ State for sectors
//   const [sectors, setSectors] = useState([]);
//   const [selectedSector, setSelectedSector] = useState(null);
//   const [loadingSectors, setLoadingSectors] = useState(false);

//   // State for Contact GUID
//   const [contactGuid, setContactGuid] = useState(null);
//   const [loadingContactGuid, setLoadingContactGuid] = useState(false);

//   const { user } = useAuth();

//   // State for loading dropdowns
//   const [loadingStatuses, setLoadingStatuses] = useState(false);
//   const [loadingSubStatuses, setLoadingSubStatuses] = useState(false);

//   // Selected values for React Select
//   const [selectedClient, setSelectedClient] = useState(null);
//   const [selectedProjectType, setSelectedProjectType] = useState(null);
//   const [selectedStatus, setSelectedStatus] = useState(null);
//   const [selectedSubStatus, setSelectedSubStatus] = useState(null);
//   const [selectedSalesperson, setSelectedSalesperson] = useState(null);
//   const [selectedManager, setSelectedManager] = useState(null);
//   const [selectedCompany, setSelectedCompany] = useState(null);

//   const [projectData, setProjectData] = useState({
//     prTitle: "",
//     prDescription: "",
//     prTags: "",
//     prStartDate: "",
//     prEndDate: "",
//     prClient: "",
//     prTypes: "",
//     usersId: "",
//     orgId: String(orgId) || "",
//     teamAssigned: "",
//     projectStatus: "",
//     projectSubStatus: "",
//     projectCategory: "",
//     comments: "",
//     salespersonCode: "",
//     currentProjectManager: "",
//     organisationId: user?.organisationId,
//     companyCode: "",
//     projectPriority: "",
//     projectSector: "", // ✅ Add sector field
//   });

//   // Helper function to sort options alphabetically by label
//   const sortOptions = (options) => {
//     return [...options].sort((a, b) => a.label.localeCompare(b.label));
//   };

//   // Convert data to React Select options format with display names
//   const formatSelectOptions = (data, valueKey, labelKey) => {
//     return data
//       .map((item) => ({
//         value: item[valueKey],
//         label: `${item[labelKey]}${item.code ? ` (${item.code})` : ""}`,
//         originalData: item,
//       }))
//       .sort((a, b) => a.label.localeCompare(b.label));
//   };

//   // ✅ Format sector options (sectorName as label, sectorGuid as value)
//   const sectorOptions = sectors.map((sector) => ({
//     value: sector.sectorGuid,
//     label: sector.sectorName,
//     originalData: sector,
//   }));

//   // Prepare options for different dropdowns
//   const companyOptions = formatSelectOptions(company, "companyCode", "name");
//   const customerOptions = formatSelectOptions(customer, "customerCode", "name");
//   const resourceOptions = formatSelectOptions(resource, "resourceId", "name");
//   const projectTypeOptions = formatSelectOptions(
//     projectTypes,
//     "projectTypeGUID",
//     "projectTypeDesc"
//   );
//   const statusOptions = formatSelectOptions(
//     projectStatuses,
//     "projectStatusGUID",
//     "projectStatusDesc"
//   );
//   const subStatusOptions = formatSelectOptions(
//     projectSubStatuses,
//     "projectSubStatusGUID",
//     "projectSubStatusDesc"
//   );
//   const teamOptions = teams
//     .map((team) => ({
//       value: team.teamId,
//       label: `${team.teamName} (${team.members?.length || 0} members)`,
//       teamData: team,
//     }))
//     .sort((a, b) => a.label.localeCompare(b.label));

//   // Fetch Contact GUID on component mount
//   useEffect(() => {
//     const fetchContactGuid = async () => {
//       if (orgId && userId) {
//         setLoadingContactGuid(true);
//         try {
//           const response = await getContactGuid(orgId, userId);
//           if (response && response.contactGuid) {
//             setContactGuid(response.contactGuid);
//             setProjectData((prev) => ({
//               ...prev,
//               usersId: response.contactGuid,
//             }));
//           } else {
//             console.error("No contact GUID received");
//             toast.error("Unable to fetch contact information");
//           }
//         } catch (error) {
//           console.error("Error fetching contact GUID:", error);
//           toast.error("Failed to load contact information");
//         } finally {
//           setLoadingContactGuid(false);
//         }
//       }
//     };

//     fetchContactGuid();
//   }, [orgId, userId]);

//   // ✅ Fetch sectors when orgId is available
//   useEffect(() => {
//     const fetchSectors = async () => {
//       if (orgId) {
//         setLoadingSectors(true);
//         try {
//           const sectorsData = await getSectorsByOrganisation(parseInt(orgId));
//           setSectors(sectorsData || []);
//         } catch (error) {
//           console.error("Error fetching sectors:", error);
//           toast.error("Failed to load project sectors");
//           setSectors([]);
//         } finally {
//           setLoadingSectors(false);
//         }
//       }
//     };

//     fetchSectors();
//   }, [orgId]);

//   // Generic handler for regular form inputs (text, date, select)
//   const handleChange = (e) => {
//     const { name, value } = e.target;
//     setProjectData((prev) => ({ ...prev, [name]: value }));
//   };

//   // Generic handler for React Select changes
//   const handleSelectChange = (selectedOption, fieldName, valueKey) => {
//     const value = selectedOption?.value || "";
    
//     setProjectData((prev) => ({
//       ...prev,
//       [fieldName]: value,
//     }));

//     // Update specific selected state
//     switch (fieldName) {
//       case "companyCode":
//         setSelectedCompany(selectedOption);
//         break;
//       case "prClient":
//         setSelectedClient(selectedOption);
//         break;
//       case "salespersonCode":
//         setSelectedSalesperson(selectedOption);
//         break;
//       case "currentProjectManager":
//         setSelectedManager(selectedOption);
//         break;
//       case "prTypes":
//         setSelectedProjectType(selectedOption);
//         // Reset status and sub-status when project type changes
//         setProjectData((prev) => ({
//           ...prev,
//           projectStatus: "",
//           projectSubStatus: "",
//         }));
//         setSelectedStatus(null);
//         setSelectedSubStatus(null);
//         setProjectStatuses([]);
//         setProjectSubStatuses([]);
//         if (selectedOption?.value) {
//           fetchProjectStatuses(selectedOption.value);
//         }
//         break;
//       case "projectStatus":
//         setSelectedStatus(selectedOption);
//         // Reset sub-status when status changes
//         setProjectData((prev) => ({
//           ...prev,
//           projectSubStatus: "",
//         }));
//         setSelectedSubStatus(null);
//         setProjectSubStatuses([]);
//         if (projectData.prTypes && selectedOption?.value) {
//           fetchProjectSubStatuses(projectData.prTypes, selectedOption.value);
//         }
//         break;
//       case "projectSubStatus":
//         setSelectedSubStatus(selectedOption);
//         break;
//       case "projectSector": // ✅ Handle sector selection
//         setSelectedSector(selectedOption);
//         break;
//       default:
//         break;
//     }
//   };

//   // Handle project type change (kept for compatibility)
//   const handleProjectTypeChange = (selectedOption) => {
//     handleSelectChange(selectedOption, "prTypes", "projectTypeGUID");
//   };

//   // Handle project status change
//   const handleProjectStatusChange = (selectedOption) => {
//     handleSelectChange(selectedOption, "projectStatus", "projectStatusGUID");
//   };

//   // Handle project sub-status change
//   const handleProjectSubStatusChange = (selectedOption) => {
//     handleSelectChange(selectedOption, "projectSubStatus", "projectSubStatusGUID");
//   };

//   // ✅ Handle sector change
//   const handleSectorChange = (selectedOption) => {
//     handleSelectChange(selectedOption, "projectSector", "sectorGuid");
//   };

//   // Fetch project status based on selected project type
//   const fetchProjectStatuses = async (projectTypeGuid) => {
//     if (!projectTypeGuid) {
//       setProjectStatuses([]);
//       return;
//     }

//     setLoadingStatuses(true);
//     try {
//       const statuses = await getProjectStatus(projectTypeGuid);
//       setProjectStatuses(statuses || []);
//     } catch (error) {
//       console.error("Error fetching project statuses:", error);
//       toast.error("Failed to load project statuses");
//       setProjectStatuses([]);
//     } finally {
//       setLoadingStatuses(false);
//     }
//   };

//   // Fetch project sub-status based on project type and status
//   const fetchProjectSubStatuses = async (projectTypeGuid, projectStatusGuid) => {
//     if (!projectTypeGuid || !projectStatusGuid) {
//       setProjectSubStatuses([]);
//       return;
//     }

//     setLoadingSubStatuses(true);
//     try {
//       const subStatuses = await getProjectSubStatus(projectTypeGuid, projectStatusGuid);
//       setProjectSubStatuses(subStatuses || []);
//     } finally {
//       setLoadingSubStatuses(false);
//     }
//   };

//   const fetchDropdownData = async () => {
//     try {
//       const [customersRes, resourcesRes, companiesRes, teamsData] =
//         await Promise.all([
//           fetchCustomersByOrgId(user?.organisationId),
//           fetchResourceByOrgId(user?.organisationId),
//           fetchCompanyCodesById(user?.organisationId),
//           getTeamsWithMembersByOrg(user?.organisationId),
//         ]);

//       // Sort data alphabetically before setting state
//       const sortedCustomers = [...(customersRes || [])].sort((a, b) => 
//         a.name?.localeCompare(b.name)
//       );
//       const sortedResources = [...(resourcesRes || [])].sort((a, b) => 
//         a.name?.localeCompare(b.name)
//       );
//       const sortedCompanies = [...(companiesRes || [])].sort((a, b) => 
//         a.name?.localeCompare(b.name)
//       );

//       setCustomer(sortedCustomers);
//       setResource(sortedResources);
//       setCompany(sortedCompanies);
//       setTeams(teamsData || []);
//     } catch (error) {
//       console.error("Error fetching dropdown data:", error);
//       toast.error("Failed to load dropdown data");
//       setCustomer([]);
//       setResource([]);
//       setCompany([]);
//       setTeams([]);
//     }
//   };

//   const fetchProjectTypesList = async () => {
//     try {
//       const types = await getProjectTypes();
//       const sortedTypes = [...(types || [])].sort((a, b) => 
//         a.projectTypeDesc?.localeCompare(b.projectTypeDesc)
//       );
//       setProjectTypes(sortedTypes);
//     } catch (error) {
//       console.error("Error fetching project types:", error);
//       toast.error("Failed to load project types");
//       setProjectTypes([]);
//     }
//   };

//   useEffect(() => {
//     fetchDropdownData();
//     fetchProjectTypesList();
//   }, []);

//   // Handle team selection
//   const handleTeamChange = (selectedOption) => {
//     setSelectedTeam(selectedOption);
//     setProjectData((prev) => ({
//       ...prev,
//       teamAssigned: selectedOption?.value || "",
//     }));
//     setHoveredTeam(null);
//   };

//   // Handle mouse enter for tooltip
//   const handleMouseEnter = (event, teamData) => {
//     if (hoverTimeoutRef.current) {
//       clearTimeout(hoverTimeoutRef.current);
//     }

//     const rect = event.currentTarget.getBoundingClientRect();
//     setTooltipPosition({
//       top: rect.top - 10,
//       left: rect.left + rect.width + 10,
//     });
//     setHoveredTeam(teamData);
//   };

//   const handleMouseLeave = () => {
//     hoverTimeoutRef.current = setTimeout(() => {
//       setHoveredTeam(null);
//     }, 100);
//   };

//   // Handle dropdown open/close
//   const handleMenuOpen = () => {
//     setIsDropdownOpen(true);
//   };

//   const handleMenuClose = () => {
//     setIsDropdownOpen(false);
//     setHoveredTeam(null);
//     if (hoverTimeoutRef.current) {
//       clearTimeout(hoverTimeoutRef.current);
//     }
//   };

//   // Custom option component with hover tooltip for teams
//   const CustomOption = (props) => {
//     const { data, innerRef, innerProps, isFocused } = props;

//     return (
//       <div
//         ref={innerRef}
//         {...innerProps}
//         style={{
//           padding: "10px",
//           backgroundColor: isFocused ? "#f0f0f0" : "white",
//           cursor: "pointer",
//           position: "relative",
//         }}
//         onMouseEnter={(e) => handleMouseEnter(e, data.teamData)}
//         onMouseLeave={handleMouseLeave}
//       >
//         <div
//           style={{
//             display: "flex",
//             justifyContent: "space-between",
//             alignItems: "center",
//           }}
//         >
//           <span>
//             <FaUsers style={{ marginRight: "8px", color: "#007bff" }} />
//             {data.label}
//           </span>
//           <FaInfoCircle style={{ color: "#6c757d", fontSize: "12px" }} />
//         </div>
//       </div>
//     );
//   };

//   // Custom single value display for teams
//   const CustomSingleValue = ({ data }) => (
//     <div style={{ display: "flex", alignItems: "center" }}>
//       <FaUsers style={{ marginRight: "8px", color: "#007bff" }} />
//       <span>{data.label}</span>
//     </div>
//   );

//   // Render team members tooltip
//   const renderTeamMembersTooltip = (team) => {
//     if (!team) return null;

//     return (
//       <Card
//         style={{
//           backgroundColor: "white",
//           border: "1px solid #ddd",
//           borderRadius: "8px",
//           boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
//           minWidth: "320px",
//           maxWidth: "450px",
//         }}
//       >
//         <Card.Header
//           style={{
//             backgroundColor: "#007bff",
//             color: "white",
//             fontWeight: "bold",
//             padding: "10px 15px",
//           }}
//         >
//           <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
//             <FaUsers />
//             <span>
//               {team.teamName} - Members ({team.members?.length || 0})
//             </span>
//           </div>
//         </Card.Header>
//         <Card.Body
//           style={{ maxHeight: "350px", overflowY: "auto", padding: "10px" }}
//         >
//           {team.members && team.members.length > 0 ? (
//             <div style={{ fontSize: "13px" }}>
//               {team.members.map((member, index) => (
//                 <div
//                   key={member.memberId}
//                   style={{
//                     padding: "10px",
//                     borderBottom:
//                       index < team.members.length - 1
//                         ? "1px solid #e9ecef"
//                         : "none",
//                     transition: "background-color 0.2s",
//                   }}
//                   onMouseEnter={(e) => {
//                     e.currentTarget.style.backgroundColor = "#f8f9fa";
//                   }}
//                   onMouseLeave={(e) => {
//                     e.currentTarget.style.backgroundColor = "transparent";
//                   }}
//                 >
//                   <div
//                     style={{
//                       display: "flex",
//                       alignItems: "center",
//                       gap: "10px",
//                     }}
//                   >
//                     <div
//                       style={{
//                         width: "32px",
//                         height: "32px",
//                         borderRadius: "50%",
//                         backgroundColor: "#e9ecef",
//                         display: "flex",
//                         alignItems: "center",
//                         justifyContent: "center",
//                         fontWeight: "bold",
//                         color: "#495057",
//                       }}
//                     >
//                       {member.memberId || index + 1}
//                     </div>
//                     <div style={{ flex: 1 }}>
//                       <div style={{ fontWeight: "600", color: "#212529" }}>
//                         {member.memberName}
//                       </div>
//                       {member.emailId && (
//                         <div
//                           style={{
//                             fontSize: "11px",
//                             color: "#6c757d",
//                             marginTop: "2px",
//                           }}
//                         >
//                           {member.emailId}
//                         </div>
//                       )}
//                     </div>
//                   </div>
//                 </div>
//               ))}
//             </div>
//           ) : (
//             <p
//               style={{
//                 textAlign: "center",
//                 color: "#999",
//                 margin: 0,
//                 padding: "20px",
//               }}
//             >
//               No members in this team
//             </p>
//           )}
//         </Card.Body>
//       </Card>
//     );
//   };

//   const handleSubmit = async (e) => {
//     e.preventDefault();

//     if (!contactGuid) {
//       toast.error("Contact information not loaded. Please refresh and try again.");
//       return;
//     }

//     const submitData = {
//       ...projectData,
//       usersId: contactGuid,
//     };

//     if (
//       !submitData.prTitle ||
//       !submitData.prDescription ||
//       !submitData.prStartDate ||
//       !submitData.prEndDate ||
//       !submitData.projectCategory ||
//       !submitData.companyCode ||
//       !submitData.projectPriority ||
//       !submitData.prTypes ||
//       !submitData.projectStatus ||
//       !submitData.currentProjectManager
//     ) {
//       toast.error("Please fill all required fields");
//       return;
//     }

//     if (submitData.projectCategory === "External") {
//       if (!submitData.prClient) {
//         toast.error("Please select a client for external project");
//         return;
//       }
//       if (!submitData.salespersonCode) {
//         toast.error("Please select a salesperson for external project");
//         return;
//       }
//     }

//     setLoading(true);
//     try {
//       const response = await saveProjectDetails(submitData, contactGuid);

//       if (response && response.projectId && response.projectName) {
//         toast.success(
//           <div>
//             <strong>✅ Project Created Successfully!</strong>
//             <br />
//             <span>
//               📋 Project ID: <strong>{response.projectId}</strong>
//             </span>
//             <br />
//             <span>
//               📝 Project Name: <strong>{response.projectName}</strong>
//             </span>
//           </div>,
//           {
//             autoClose: 5000,
//             position: "top-right",
//           }
//         );
//       } else {
//         toast.success("🦄 Project Added Successfully");
//       }

//       // Reset form
//       setProjectData({
//         prTitle: "",
//         prDescription: "",
//         prTags: "",
//         prStartDate: "",
//         prEndDate: "",
//         prClient: "",
//         prTypes: "",
//         usersId: contactGuid,
//         orgId: String(orgId) || "",
//         teamAssigned: "",
//         projectStatus: "",
//         projectSubStatus: "",
//         projectCategory: "",
//         comments: "",
//         salespersonCode: "",
//         currentProjectManager: "",
//         organisationId: user?.organisationId,
//         companyCode: "",
//         projectPriority: "",
//         projectSector: "", // ✅ Reset sector
//       });
      
//       // Reset all selected values
//       setSelectedCompany(null);
//       setSelectedClient(null);
//       setSelectedSalesperson(null);
//       setSelectedManager(null);
//       setSelectedProjectType(null);
//       setSelectedStatus(null);
//       setSelectedSubStatus(null);
//       setSelectedTeam(null);
//       setSelectedSector(null); // ✅ Reset selected sector
//       setProjectStatuses([]);
//       setProjectSubStatuses([]);

//       setTimeout(() => {
//         navigate("/viewProjects");
//       }, 2000);
//     } catch (error) {
//       toast.error(`🦄 Error saving project: ${error.message || error}`);
//       console.error("Save error:", error);
//     } finally {
//       setLoading(false);
//     }
//   };

//   // Add animation styles
//   useEffect(() => {
//     const style = document.createElement("style");
//     style.textContent = `
//       @keyframes fadeIn {
//         from {
//           opacity: 0;
//           transform: translateX(-10px);
//         }
//         to {
//           opacity: 1;
//           transform: translateX(0);
//         }
//       }
//     `;
//     document.head.appendChild(style);
//     return () => {
//       document.head.removeChild(style);
//       if (hoverTimeoutRef.current) {
//         clearTimeout(hoverTimeoutRef.current);
//       }
//     };
//   }, []);

//   return (
//     <div className="main-content expand">
//       <Container fluid className="fields-container">
//         <h2 className="fields-title">Project Master</h2>

//         {(loading || loadingContactGuid) && (
//           <div className="loading-overlay">
//             <Sample14 />
//             {loadingContactGuid && (
//               <p style={{ marginTop: "10px" }}>
//                 Loading contact information...
//               </p>
//             )}
//           </div>
//         )}

//         {/* Tooltip rendered via Portal */}
//         {hoveredTeam &&
//           isDropdownOpen &&
//           createPortal(
//             <div
//               style={{
//                 position: "fixed",
//                 zIndex: 9999,
//                 top: tooltipPosition.top,
//                 left: tooltipPosition.left,
//                 animation: "fadeIn 0.2s ease-in-out",
//               }}
//               onMouseEnter={() => {
//                 if (hoverTimeoutRef.current) {
//                   clearTimeout(hoverTimeoutRef.current);
//                 }
//               }}
//               onMouseLeave={() => {
//                 setHoveredTeam(null);
//               }}
//             >
//               {renderTeamMembersTooltip(hoveredTeam)}
//             </div>,
//             document.body
//           )}

//         <Form onSubmit={handleSubmit} className="fields-form">
//           {/* Company Code and Category */}
//           <Row>
//             <Col md={4}>
//               <Form.Group className="mb-3">
//                 <Form.Label>
//                   Company Code <span className="text-red">**</span>
//                 </Form.Label>
//                 <Select
//                   options={companyOptions}
//                   value={selectedCompany}
//                   onChange={(option) => handleSelectChange(option, "companyCode")}
//                   placeholder="Search and select company..."
//                   isClearable
//                   isSearchable
//                   isDisabled={!user?.organisationId || loadingContactGuid}
//                   noOptionsMessage={() => "No companies found"}
//                   styles={{
//                     control: (base) => ({
//                       ...base,
//                       borderColor: "#ced4da",
//                       "&:hover": {
//                         borderColor: "#86b7fe",
//                       },
//                     }),
//                   }}
//                 />
//               </Form.Group>
//             </Col>

//             <Col md={4}>
//               <Form.Group className="mb-3">
//                 <Form.Label>
//                   Category <span className="text-red">**</span>
//                 </Form.Label>
//                 <Form.Select
//                   name="projectCategory"
//                   value={projectData.projectCategory}
//                   onChange={handleChange}
//                   required
//                   disabled={loadingContactGuid}
//                 >
//                   <option value="">Select Category</option>
//                   <option value="Internal">Internal</option>
//                   <option value="External">External</option>
//                 </Form.Select>
//               </Form.Group>
//             </Col>

//             {/* ✅ NEW: Project Sector Dropdown */}
//             <Col md={4}>
//               <Form.Group className="mb-3">
//                 <Form.Label>Project Sector</Form.Label>
//                 <Select
//                   options={sectorOptions}
//                   value={selectedSector}
//                   onChange={handleSectorChange}
//                   placeholder={
//                     loadingSectors
//                       ? "Loading sectors..."
//                       : "Search and select project sector..."
//                   }
//                   isClearable
//                   isSearchable
//                   isDisabled={loadingSectors || loadingContactGuid}
//                   isLoading={loadingSectors}
//                   noOptionsMessage={() =>
//                     loadingSectors ? "Loading..." : "No sectors found"
//                   }
//                   styles={{
//                     control: (base) => ({
//                       ...base,
//                       borderColor: "#ced4da",
//                       "&:hover": {
//                         borderColor: "#86b7fe",
//                       },
//                     }),
//                   }}
//                 />
//                 <small className="text-muted">
//                   Select the industry sector for this project
//                 </small>
//               </Form.Group>
//             </Col>
//           </Row>

//           {/* Basic Details */}
//           <Row>
//             <Col md={4}>
//               <Form.Group className="mb-3">
//                 <Form.Label>
//                   Project Title <span className="text-red">**</span>
//                 </Form.Label>
//                 <Form.Control
//                   type="text"
//                   name="prTitle"
//                   value={projectData.prTitle}
//                   onChange={handleChange}
//                   required
//                   disabled={loadingContactGuid}
//                 />
//               </Form.Group>
//             </Col>

//             <Col md={4}>
//               <Form.Group className="mb-3">
//                 <Form.Label>
//                   Project Description <span className="text-red">**</span>
//                 </Form.Label>
//                 <Form.Control
//                   type="text"
//                   name="prDescription"
//                   value={projectData.prDescription}
//                   onChange={handleChange}
//                   required
//                   disabled={loadingContactGuid}
//                 />
//               </Form.Group>
//             </Col>

//             <Col md={4}>
//               <Form.Group className="mb-3">
//                 <Form.Label>Project Tags</Form.Label>
//                 <Form.Control
//                   type="text"
//                   name="prTags"
//                   value={projectData.prTags}
//                   onChange={handleChange}
//                   disabled={loadingContactGuid}
//                 />
//               </Form.Group>
//             </Col>
//           </Row>

//           {/* Dates */}
//           <Row>
//             <Col md={4}>
//               <Form.Group className="mb-3">
//                 <Form.Label>
//                   Start Date <span className="text-red">**</span>
//                 </Form.Label>
//                 <Form.Control
//                   type="date"
//                   name="prStartDate"
//                   value={projectData.prStartDate}
//                   onChange={handleChange}
//                   required
//                   disabled={loadingContactGuid}
//                 />
//               </Form.Group>
//             </Col>

//             <Col md={4}>
//               <Form.Group className="mb-3">
//                 <Form.Label>
//                   End Date <span className="text-red">**</span>
//                 </Form.Label>
//                 <Form.Control
//                   type="date"
//                   name="prEndDate"
//                   value={projectData.prEndDate}
//                   onChange={handleChange}
//                   required
//                   disabled={loadingContactGuid}
//                 />
//               </Form.Group>
//             </Col>

//             {projectData.projectCategory !== "Internal" && (
//               <Col md={4}>
//                 <Form.Group className="mb-3">
//                   <Form.Label>
//                     Client <span className="text-red">**</span>
//                   </Form.Label>
//                   <Select
//                     options={customerOptions}
//                     value={selectedClient}
//                     onChange={(option) => handleSelectChange(option, "prClient")}
//                     placeholder="Search and select client..."
//                     isClearable
//                     isSearchable
//                     isDisabled={!user?.organisationId || loadingContactGuid}
//                     required={projectData.projectCategory === "External"}
//                     noOptionsMessage={() => "No clients found"}
//                     styles={{
//                       control: (base) => ({
//                         ...base,
//                         borderColor: "#ced4da",
//                         "&:hover": {
//                           borderColor: "#86b7fe",
//                         },
//                       }),
//                     }}
//                   />
//                 </Form.Group>
//               </Col>
//             )}
//           </Row>

//           {/* Type, Team, Priority */}
//           <Row>
//             <Col md={4}>
//               <Form.Group className="mb-3">
//                 <Form.Label>
//                   Project Type <span className="text-red">**</span>
//                 </Form.Label>
//                 <Select
//                   options={projectTypeOptions}
//                   value={selectedProjectType}
//                   onChange={handleProjectTypeChange}
//                   placeholder="Search and select project type..."
//                   isClearable
//                   isSearchable
//                   isDisabled={!user?.organisationId || loadingContactGuid}
//                   noOptionsMessage={() => "No project types found"}
//                   styles={{
//                     control: (base) => ({
//                       ...base,
//                       borderColor: "#ced4da",
//                       "&:hover": {
//                         borderColor: "#86b7fe",
//                       },
//                     }),
//                   }}
//                 />
//               </Form.Group>
//             </Col>

//             <Col md={4}>
//               <Form.Group className="mb-3">
//                 <Form.Label>Team Assigned</Form.Label>
//                 <div ref={teamSelectRef}>
//                   <Select
//                     options={teamOptions}
//                     value={selectedTeam}
//                     onChange={handleTeamChange}
//                     onMenuOpen={handleMenuOpen}
//                     onMenuClose={handleMenuClose}
//                     placeholder="Search and select team..."
//                     isClearable
//                     isSearchable
//                     isDisabled={loadingContactGuid}
//                     components={{
//                       Option: CustomOption,
//                       SingleValue: CustomSingleValue,
//                     }}
//                     styles={{
//                       control: (base) => ({
//                         ...base,
//                         minHeight: "38px",
//                         borderColor: "#ced4da",
//                         "&:hover": {
//                           borderColor: "#86b7fe",
//                         },
//                       }),
//                       option: (base, state) => ({
//                         ...base,
//                         backgroundColor: state.isFocused ? "#f0f0f0" : "white",
//                         "&:active": {
//                           backgroundColor: "#e0e0e0",
//                         },
//                       }),
//                       menu: (base) => ({
//                         ...base,
//                         zIndex: 9998,
//                       }),
//                     }}
//                   />
//                 </div>
//                 <small className="text-muted">
//                   Hover over any team to see its members
//                 </small>
//               </Form.Group>
//             </Col>

//             <Col md={4}>
//               <Form.Group className="mb-3">
//                 <Form.Label>
//                   Project Priority <span className="text-red">**</span>
//                 </Form.Label>
//                 <Form.Select
//                   name="projectPriority"
//                   value={projectData.projectPriority}
//                   onChange={handleChange}
//                   required
//                   disabled={loadingContactGuid}
//                 >
//                   <option value="">Select Priority</option>
//                   <option value="Very High">Very High</option>
//                   <option value="High">High</option>
//                   <option value="Medium">Medium</option>
//                   <option value="Low">Low</option>
//                 </Form.Select>
//               </Form.Group>
//             </Col>
//           </Row>

//           {/* Status and Sub Status */}
//           <Row>
//             <Col md={4}>
//               <Form.Group className="mb-3">
//                 <Form.Label>
//                   Status <span className="text-red">**</span>
//                 </Form.Label>
//                 <Select
//                   options={statusOptions}
//                   value={selectedStatus}
//                   onChange={handleProjectStatusChange}
//                   placeholder={
//                     loadingStatuses
//                       ? "Loading statuses..."
//                       : !projectData.prTypes
//                       ? "Select project type first"
//                       : "Search and select status..."
//                   }
//                   isClearable
//                   isSearchable
//                   isDisabled={
//                     !projectData.prTypes || loadingStatuses || loadingContactGuid
//                   }
//                   isLoading={loadingStatuses}
//                   noOptionsMessage={() => 
//                     loadingStatuses ? "Loading..." : "No statuses found"
//                   }
//                   styles={{
//                     control: (base) => ({
//                       ...base,
//                       borderColor: "#ced4da",
//                       "&:hover": {
//                         borderColor: "#86b7fe",
//                       },
//                     }),
//                   }}
//                 />
//               </Form.Group>
//             </Col>

//             <Col md={4}>
//               <Form.Group className="mb-3">
//                 <Form.Label>Sub Status</Form.Label>
//                 <Select
//                   options={subStatusOptions}
//                   value={selectedSubStatus}
//                   onChange={handleProjectSubStatusChange}
//                   placeholder={
//                     loadingSubStatuses
//                       ? "Loading sub-statuses..."
//                       : !projectData.projectStatus
//                       ? "Select status first"
//                       : "Search and select sub-status..."
//                   }
//                   isClearable
//                   isSearchable
//                   isDisabled={
//                     !projectData.projectStatus || loadingSubStatuses || loadingContactGuid
//                   }
//                   isLoading={loadingSubStatuses}
//                   noOptionsMessage={() =>
//                     loadingSubStatuses ? "Loading..." : "No sub-statuses found"
//                   }
//                   styles={{
//                     control: (base) => ({
//                       ...base,
//                       borderColor: "#ced4da",
//                       "&:hover": {
//                         borderColor: "#86b7fe",
//                       },
//                     }),
//                   }}
//                 />
//               </Form.Group>
//             </Col>

//             <Col md={4}>
//               <Form.Group className="mb-3">
//                 <Form.Label>Comments</Form.Label>
//                 <Form.Control
//                   type="text"
//                   name="comments"
//                   value={projectData.comments}
//                   onChange={handleChange}
//                   disabled={loadingContactGuid}
//                 />
//               </Form.Group>
//             </Col>
//           </Row>

//           <Row>
//             {projectData.projectCategory !== "Internal" && (
//               <Col md={4}>
//                 <Form.Group className="mb-3">
//                   <Form.Label>
//                     Salesperson <span className="text-red">**</span>
//                   </Form.Label>
//                   <Select
//                     options={resourceOptions}
//                     value={selectedSalesperson}
//                     onChange={(option) => handleSelectChange(option, "salespersonCode")}
//                     placeholder="Search and select salesperson..."
//                     isClearable
//                     isSearchable
//                     isDisabled={!user?.organisationId || loadingContactGuid}
//                     required={projectData.projectCategory === "External"}
//                     noOptionsMessage={() => "No salespersons found"}
//                     styles={{
//                       control: (base) => ({
//                         ...base,
//                         borderColor: "#ced4da",
//                         "&:hover": {
//                           borderColor: "#86b7fe",
//                         },
//                       }),
//                     }}
//                   />
//                 </Form.Group>
//               </Col>
//             )}

//             <Col md={4}>
//               <Form.Group className="mb-3">
//                 <Form.Label>
//                   Project Manager <span className="text-red">**</span>
//                 </Form.Label>
//                 <Select
//                   options={resourceOptions}
//                   value={selectedManager}
//                   onChange={(option) => handleSelectChange(option, "currentProjectManager")}
//                   placeholder="Search and select project manager..."
//                   isClearable
//                   isSearchable
//                   isDisabled={!user?.organisationId || loadingContactGuid}
//                   required
//                   noOptionsMessage={() => "No managers found"}
//                   styles={{
//                     control: (base) => ({
//                       ...base,
//                       borderColor: "#ced4da",
//                       "&:hover": {
//                         borderColor: "#86b7fe",
//                       },
//                     }),
//                   }}
//                 />
//               </Form.Group>
//             </Col>
//           </Row>

//           {/* Hidden field to show Contact GUID info */}
//           {contactGuid && (
//             <Row>
//               <Col md={12}>
//                 <small className="text-muted">
//                   Contact ID: {contactGuid.substring(0, 8)}... (Auto-loaded)
//                 </small>
//               </Col>
//             </Row>
//           )}

//           {/* Submit Button */}
//           <div className="fields-btn-container">
//             <Button
//               variant="primary"
//               type="submit"
//               className="fields-submit"
//               disabled={loading || loadingContactGuid || !contactGuid}
//             >
//               {loading ? "Saving..." : "Save Project"}
//             </Button>
//           </div>
//         </Form>
//       </Container>
//     </div>
//   );
// };

// export default ProjectMaster;
